import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { UploadCloud, FileText, PenLine, Loader2, X, CheckCircle2, AlertTriangle, ShieldAlert, BookOpen } from "lucide-react";
import { toast } from "sonner";

const ACCEPT = ".txt,.md,.pdf,.docx,.html,.htm";

export default function StoryUpload() {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const addFiles = (list) => {
    const valid = Array.from(list).filter((f) => /\.(txt|md|pdf|docx|html?|text)$/i.test(f.name));
    if (valid.length !== list.length) toast.error("Only .txt, .pdf, .docx and .html files are supported");
    setFiles((prev) => [...prev, ...valid]);
  };

  const onDrop = (e) => { e.preventDefault(); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); };

  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));

  const pollJob = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/story-jobs/${id}`);
        setJob(data);
        if (data.status === "completed") {
          clearInterval(pollRef.current);
          setSubmitting(false);
        }
      } catch {
        clearInterval(pollRef.current);
        setSubmitting(false);
      }
    }, 2000);
  };

  const submit = async () => {
    if (files.length === 0 && text.trim().split(/\s+/).length < 20) {
      toast.error("Add a file or paste at least ~20 words");
      return;
    }
    setSubmitting(true);
    setJob(null);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (text.trim()) fd.append("text", text.trim());
    if (title.trim()) fd.append("title", title.trim());
    if (tags.trim()) fd.append("tags", tags.trim());
    try {
      const { data } = await api.post("/stories/upload", fd);
      toast.success(`Scanning ${data.queued} submission${data.queued === 1 ? "" : "s"}…`);
      setJob({ id: data.job_id, total: data.queued, done: 0, status: "running", items: [] });
      pollJob(data.job_id);
    } catch (err) {
      setSubmitting(false);
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    }
  };

  const reset = () => { setFiles([]); setText(""); setTitle(""); setTags(""); setJob(null); };

  const statusMeta = (st) => {
    if (st === "published") return { icon: CheckCircle2, color: "#31A24C", label: "Published" };
    if (st === "review") return { icon: AlertTriangle, color: "#F1A817", label: "Held for review" };
    if (st === "blocked") return { icon: ShieldAlert, color: "#E60023", label: "Blocked (policy)" };
    return { icon: Loader2, color: "#00E5FF", label: "Scanning…" };
  };

  return (
    <div className="max-w-[820px] mx-auto px-4 sm:px-6 pt-16 pb-20" data-testid="story-upload-page">
      <div className="flex items-center gap-3 mb-2">
        <PenLine className="text-[#00E5FF]" size={28} />
        <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tighter text-white">Add a story</h1>
      </div>
      <p className="text-[#A1A1AA] text-sm mb-8">Upload <span className="text-white">.txt, .pdf, .docx, .html</span> files (single or in bulk) or paste your text below. Our AI writes a title, summary, tags &amp; category and screens every submission for safety.</p>

      {!job && (
        <>
          {/* File dropzone */}
          <div
            data-testid="story-dropzone"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="border-2 border-dashed border-[#1C1C1F] hover:border-[#00E5FF]/50 rounded-2xl p-8 text-center cursor-pointer transition-colors bg-[#0A0A0A]"
          >
            <UploadCloud className="text-[#00E5FF] mx-auto mb-3" size={34} />
            <p className="text-white font-semibold">Drop files or click to browse</p>
            <p className="text-[#52525B] text-xs mt-1 font-mono">.txt · .pdf · .docx · .html · up to 15MB each</p>
            <input ref={inputRef} data-testid="story-file-input" type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => e.target.files && addFiles(e.target.files)} />
          </div>

          {files.length > 0 && (
            <div className="mt-4 flex flex-col gap-2" data-testid="story-file-list">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-3 bg-[#141414] border border-[#1C1C1F] rounded-xl px-4 py-2.5">
                  <FileText size={16} className="text-[#00E5FF] shrink-0" />
                  <span className="text-white text-sm truncate flex-1">{f.name}</span>
                  <span className="text-[#52525B] text-xs font-mono">{(f.size / 1024).toFixed(0)} KB</span>
                  <button data-testid={`story-file-remove-${i}`} onClick={() => removeFile(i)} className="text-[#52525B] hover:text-[#E60023]"><X size={16} /></button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 my-6">
            <div className="h-px bg-[#1C1C1F] flex-1" />
            <span className="text-[#52525B] font-mono text-[11px] uppercase tracking-widest">or paste text</span>
            <div className="h-px bg-[#1C1C1F] flex-1" />
          </div>

          <input
            data-testid="story-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Optional title (AI writes one if left blank)"
            className="w-full bg-[#141414] border border-[#1C1C1F] text-white px-4 py-3 rounded-xl text-sm mb-3 focus:outline-none focus:border-[#00E5FF] placeholder:text-[#52525B]"
          />
          <textarea
            data-testid="story-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            placeholder="Paste your story, memoir or scene here…"
            className="w-full bg-[#141414] border border-[#1C1C1F] text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-[#00E5FF] placeholder:text-[#52525B] resize-y leading-relaxed"
          />
          <input
            data-testid="story-tags-input"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="Extra tags, comma-separated (optional)"
            className="w-full bg-[#141414] border border-[#1C1C1F] text-white px-4 py-3 rounded-xl text-sm mt-3 focus:outline-none focus:border-[#00E5FF] placeholder:text-[#52525B]"
          />

          <button
            data-testid="story-submit-btn"
            onClick={submit}
            disabled={submitting}
            className="w-full mt-6 flex items-center justify-center gap-2 bg-[#00E5FF] text-black font-heading uppercase tracking-widest text-sm py-3.5 rounded-xl hover:bg-[#00c9e0] transition-colors disabled:opacity-50"
          >
            {submitting ? <Loader2 className="animate-spin" size={18} /> : <PenLine size={18} />} Scan &amp; publish
          </button>
          <p className="text-[#52525B] text-[11px] text-center mt-3 leading-relaxed">By submitting you confirm all people depicted are adults (18+). Content involving minors is prohibited and automatically blocked.</p>
        </>
      )}

      {job && (
        <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-6" data-testid="story-job-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-heading uppercase tracking-tight text-xl">
              {job.status === "completed" ? "Scan complete" : "Scanning…"}
            </h2>
            <span className="text-[#52525B] font-mono text-xs">{job.done}/{job.total}</span>
          </div>
          <div className="h-2 bg-[#141414] rounded-full overflow-hidden mb-5">
            <div className="h-full bg-[#00E5FF] transition-all duration-500" style={{ width: `${job.total ? (job.done / job.total) * 100 : 0}%` }} />
          </div>

          <div className="flex flex-col gap-2" data-testid="story-job-items">
            {(job.items || []).map((it, i) => {
              const m = statusMeta(it.status);
              const Icon = m.icon;
              return (
                <div key={i} className="flex items-center gap-3 bg-[#141414] border border-[#1C1C1F] rounded-xl px-4 py-3">
                  <Icon size={17} className={it.status === "scanning" ? "animate-spin" : ""} style={{ color: m.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{it.title}</p>
                    {it.safety_reason && it.status !== "scanning" && <p className="text-[#52525B] text-[11px] truncate">{it.safety_reason}</p>}
                  </div>
                  <span className="text-xs font-mono shrink-0" style={{ color: m.color }}>{m.label}</span>
                  {it.status === "published" && it.story_id && (
                    <Link to={`/stories/${it.story_id}`} data-testid={`story-job-open-${i}`} className="text-[#00E5FF] text-xs font-semibold hover:underline shrink-0">Read →</Link>
                  )}
                </div>
              );
            })}
          </div>

          {job.status === "completed" && (
            <div className="flex gap-3 mt-6">
              <button data-testid="story-job-another" onClick={reset} className="flex-1 py-3 rounded-xl border border-[#1C1C1F] text-white hover:border-[#00E5FF] text-sm font-semibold transition-colors flex items-center justify-center gap-2"><PenLine size={15} /> Add another</button>
              <button data-testid="story-job-browse" onClick={() => nav("/stories")} className="flex-1 py-3 rounded-xl bg-[#00E5FF] text-black text-sm font-semibold hover:bg-[#00c9e0] transition-colors flex items-center justify-center gap-2"><BookOpen size={15} /> Browse stories</button>
            </div>
          )}
          {job.blocked > 0 && (
            <p className="text-[#E60023] text-xs mt-4 flex items-center gap-2"><ShieldAlert size={14} /> {job.blocked} submission{job.blocked === 1 ? " was" : "s were"} blocked for depicting minors and not saved.</p>
          )}
          {job.review > 0 && (
            <p className="text-[#F1A817] text-xs mt-2 flex items-center gap-2"><AlertTriangle size={14} /> {job.review} submission{job.review === 1 ? " is" : "s are"} held for moderator review before going public.</p>
          )}
        </div>
      )}
    </div>
  );
}
